import * as Sentry from "@sentry/react";
import _ from "lodash";

/**
 * Replaces {{prefix:path}} placeholders using values from the provided
 * context dictionary. Three prefixes: `var` (computed form variables),
 * `context` (resolved subject facts), and `field` (the form's own answers
 * — v2 passes the answers as of the last screen advance, so copy follows
 * what was entered without shifting under the customer as they type).
 *
 * A token may carry an inline fallback after a pipe —
 * `{{field:insured_name.first_name|your loved one}}` — used when the value
 * is missing (for `field`, an empty answer counts as missing). An absent
 * `field` value is a normal mid-form state and falls back quietly; an
 * absent `var`/`context` value is a config bug and still reports.
 *
 * @param input - The input string containing placeholders.
 * @param context - An object containing key-value pairs for substitution.
 * @returns The input string with all placeholders substituted.
 */
export function substituteVariables(
  input: string,
  context: { [key: string]: any },
): string {
  // {{prefix:dotted.path}} with an optional |fallback before the closing braces
  const variablePattern =
    /{{\s*(var|context|field)\s*:\s*([a-zA-Z0-9_.]+)\s*(?:\|([^}]*))?}}/g;

  return input.replace(
    variablePattern,
    (match, prefix, variablePath, fallback) => {
      const sourceByPrefix: { [key: string]: unknown } = {
        var: context.variables,
        context: context.context,
        field: context.answers,
      };
      const source = sourceByPrefix[prefix];
      const value = source ? _.get(source, variablePath) : undefined;

      if (prefix === "field") {
        // An unanswered field is a normal state, not a config bug.
        if (value !== undefined && value !== null && value !== "") {
          return String(value);
        }
        return fallback !== undefined ? fallback.trim() : "____";
      }

      if (value !== undefined) {
        return String(value);
      }
      if (fallback !== undefined) {
        return fallback.trim();
      }

      Sentry.captureMessage(
        `Form variable substitution missing substitute: ${prefix}:${variablePath}`,
      );
      // If variable not found, return placeholder
      console.warn(
        `Variable "${prefix}:${variablePath}" not found in contextData.`,
      );
      // NOTE: Or is it better to leave variable?
      return "____";
    },
  );
}
